import { ChevronDown } from "@untitledui/icons";
import { type SelectHTMLAttributes, useId } from "react";
import type { DisplayFieldFeedback as DisplayFieldFeedbackType } from "@/components/Atoms/DisplayField/display-field.shared";
import { HintText } from "@/components/base/input/hint-text";
import { isRequiredFieldFilled } from "@/components/base/input/field-label";
import { LabelWithFeedback } from "@/components/base/input/label-with-feedback";
import { cx } from "@/utils/cx";

interface NativeSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
	label?: string;
	hint?: string;
	/** Shows the red required asterisk on the label when true. */
	isRequired?: boolean;
	/** Auto-save FeedbackIndicator in the label row (Figma Input Auto-save). */
	feedback?: DisplayFieldFeedbackType;
	selectClassName?: string;
	options: {
		label: string;
		value: string;
		disabled?: boolean;
		hidden?: boolean;
	}[];
}

export const NativeSelect = ({
	label,
	hint,
	isRequired,
	feedback,
	options,
	className,
	selectClassName,
	style,
	...props
}: NativeSelectProps) => {
	const id = useId();
	const selectId = `select-native-${id}`;
	const labelId = `${selectId}-label`;
	const hintId = `select-native-hint-${id}`;
	// The Windows popup copies the select's own color and drops CSS variables,
	// so a variable can paint the label the same color as the menu.
	const menuText = props.disabled ? "rgb(164 167 174)" : "rgb(33 58 83)";
	const menuBackground = "rgb(255 255 255)";

	return (
		<div className={cx("w-full in-data-input-wrapper:w-max", className)}>
			<LabelWithFeedback
				label={label}
				isRequired={isRequired}
				isFilled={isRequiredFieldFilled(props.value)}
				feedback={feedback}
				className="mb-1"
				labelProps={{ htmlFor: selectId, id: labelId }}
			/>

			<div className="relative grid w-full items-center">
				<select
					{...props}
					style={{
						...style,
						colorScheme: "light",
					}}
					id={selectId}
					required={isRequired || props.required}
					aria-required={isRequired || props.required || undefined}
					aria-describedby={hintId}
					aria-labelledby={label ? labelId : undefined}
					className={cx(
						"appearance-none rounded-lg bg-primary px-3.5 py-2.5 text-md font-medium text-primary shadow-xs ring-1 ring-primary outline-hidden transition duration-100 ease-linear ring-inset [color-scheme:light] placeholder:text-fg-quaternary focus-visible:ring-2 focus-visible:ring-brand_alt cursor-pointer disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled",
						// Styles when the select is within an `InputGroup`
						"in-data-input-wrapper:flex in-data-input-wrapper:h-full in-data-input-wrapper:gap-1 in-data-input-wrapper:bg-inherit in-data-input-wrapper:px-3 in-data-input-wrapper:py-2 in-data-input-wrapper:font-normal in-data-input-wrapper:text-tertiary in-data-input-wrapper:shadow-none in-data-input-wrapper:ring-transparent",
						// Styles for the select when `TextField` is disabled
						"in-data-input-wrapper:group-disabled:pointer-events-none in-data-input-wrapper:group-disabled:cursor-not-allowed in-data-input-wrapper:group-disabled:bg-transparent in-data-input-wrapper:group-disabled:text-disabled",
						// Common styles for sizes and border radius within `InputGroup`
						"in-data-input-wrapper:in-data-leading:rounded-r-none in-data-input-wrapper:in-data-trailing:rounded-l-none in-data-input-wrapper:in-data-[input-size=md]:py-2.5 in-data-input-wrapper:in-data-leading:in-data-[input-size=md]:pl-3.5 in-data-input-wrapper:in-data-[input-size=sm]:py-2 in-data-input-wrapper:in-data-[input-size=sm]:pl-3",
						// For "leading" dropdown within `InputGroup`
						"in-data-input-wrapper:in-data-leading:in-data-[input-size=md]:pr-4.5 in-data-input-wrapper:in-data-leading:in-data-[input-size=sm]:pr-4.5",
						// For "trailing" dropdown within `InputGroup`
						"in-data-input-wrapper:in-data-trailing:in-data-[input-size=md]:pr-8 in-data-input-wrapper:in-data-trailing:in-data-[input-size=sm]:pr-7.5",
						selectClassName,
					)}
				>
					{options.map((opt, index) => (
						<option
							key={opt.value === "" ? `__empty__-${index}` : `${opt.value}-${index}`}
							value={opt.value}
							disabled={opt.disabled}
							hidden={opt.hidden}
							style={{
								color: opt.disabled ? "rgb(164 167 174)" : menuText,
								backgroundColor: menuBackground,
							}}
						>
							{opt.label}
						</option>
					))}
				</select>
				<ChevronDown
					aria-hidden="true"
					className="pointer-events-none absolute right-3.5 size-5 text-fg-quaternary in-data-input-wrapper:right-0 in-data-input-wrapper:size-4 in-data-input-wrapper:stroke-[2.625px] in-data-input-wrapper:in-data-trailing:in-data-[input-size=sm]:right-3"
				/>
			</div>

			{hint && (
				<HintText className="mt-2" id={hintId}>
					{hint}
				</HintText>
			)}
		</div>
	);
};
